import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTrail, animated, useSpring } from '@react-spring/web';
import api from "../api/axios";
import PaymentForm from "../components/PaymentForm";
import SearchFilter from "../components/SearchFilter";
import BulkActions from "../components/BulkActions";
import ExportData from "../components/ExportData";
import StatusBadge from "../components/StatusBadge";
import { exportSingleRecord } from "../utils/pdfExport";
import PageTransition from '../components/PageTransition';
import LoadingSpinner from '../components/LoadingSpinner';
import { smoothTransitions } from '../utils/smoothTransitions';
import { useOptimizedAnimation } from '../hooks/useOptimizedAnimation';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function Payments() {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const { getOptimizedVariants } = useOptimizedAnimation();

  // Animation hooks
  const trail = useTrail(filteredPayments.length, {
    from: { opacity: 0, transform: 'translate3d(0,40px,0)' },
    to: { opacity: 1, transform: 'translate3d(0,0px,0)' },
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const headerSpring = useSpring({
    from: { opacity: 0, transform: 'translate3d(0,-50px,0)' },
    to: { opacity: 1, transform: 'translate3d(0,0px,0)' },
    config: { tension: 300, friction: 30 }
  });

  const fetchPayments = () => {
    api.get("/payments")
      .then((res) => {
        const paymentsData = res.data?.data || res.data || [];
        console.log('Payments data:', paymentsData);
        if (paymentsData.length > 0) {
          console.log('First payment sample:', paymentsData[0]);
        }
        setPayments(paymentsData);
        setFilteredPayments(paymentsData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching payments:", err);
        setPayments([]);
        setFilteredPayments([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleEdit = (payment) => {
    setEditPayment(payment);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (confirm(t('confirm_delete_payment'))) {
      try {
        const numericId = Number(id);
        if (isNaN(numericId) || numericId <= 0) {
          throw new Error('Invalid payment ID');
        }
        const sanitizedId = encodeURIComponent(String(numericId));
        await api.delete(`/payments/${sanitizedId}`);
        fetchPayments();
      } catch (error) {
        console.error('Error deleting payment:', error);
      }
    }
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditPayment(null);
    fetchPayments();
  };

  const handleSearch = (searchTerm) => {
    const filtered = payments.filter(payment =>
      payment.payment_method.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.booking?.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.booking?.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPayments(filtered);
  };

  const getBookingCode = (bookingId) => `BKG${String(bookingId).padStart(3, '0')}`;

  const getStorageUrl = (path) => {
    if (!path) return null;
    const base = import.meta.env.VITE_API_URL.replace('/api', '');
    // Remove leading 'storage/' if already present
    const clean = path.replace(/^\/?(storage\/)?/, '');
    return `${base}/storage/${clean}`;
  };

  const handleFilter = (filterValue) => {
    if (!filterValue) {
      setFilteredPayments(payments);
    } else {
      const filtered = payments.filter(payment => payment.status === filterValue);
      setFilteredPayments(filtered);
    }
  };

  const handleBulkAction = async (action, selectedIds) => {
    if (action === 'delete') {
      if (confirm(`${t('delete')} ${selectedIds.length} ${t('selected')} ${t('payments')}?`)) {
        try {
          const validIds = selectedIds.filter(id => {
            const numericId = Number(id);
            return !isNaN(numericId) && numericId > 0;
          });
          await Promise.all(validIds.map(id => {
            const sanitizedId = encodeURIComponent(String(Number(id)));
            return api.delete(`/payments/${sanitizedId}`);
          }));
          fetchPayments();
          setSelectedPayments([]);
        } catch (error) {
          console.error('Bulk delete failed:', error);
        }
      }
    } else if (action === 'complete') {
      try {
        const validIds = selectedIds.filter(id => {
          const numericId = Number(id);
          return !isNaN(numericId) && numericId > 0;
        });
        await Promise.all(validIds.map(id => {
          const sanitizedId = encodeURIComponent(String(Number(id)));
          return api.put(`/payments/${sanitizedId}`, { status: 'completed' });
        }));
        fetchPayments();
        setSelectedPayments([]);
      } catch (error) {
        console.error('Bulk complete failed:', error);
      }
    }
  };

  const handlePaymentSelect = (paymentId) => {
    setSelectedPayments(prev =>
      prev.includes(paymentId)
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      const numericId = Number(id);
      if (isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid payment ID');
      }
      const sanitizedId = encodeURIComponent(String(numericId));
      await api.put(`/payments/${sanitizedId}`, { status });
      fetchPayments();
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const bulkActions = [
    { key: 'delete', label: t('delete'), className: 'bg-red-500 text-white hover:bg-red-600' },
    { key: 'complete', label: t('mark_completed'), className: 'bg-green-500 text-white hover:bg-green-600' }
  ];

  const statusFilters = [
    { value: 'pending', label: t('pending') },
    { value: 'completed', label: t('completed') },
    { value: 'failed', label: t('failed') },
    { value: 'refunded', label: t('refunded') }
  ];

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-96">
          <LoadingSpinner variant="payments" size="large" />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        {/* Header */}
        <animated.div
          style={headerSpring}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6`}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}
              >
                {t('payment_management')}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mt-1`}
              >
                {t('manage_all_payments')}
              </motion.p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ExportData data={filteredPayments} filename="payments" language={language} />
              </motion.div>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowForm(true)}
                className={`${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'} text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center`}
              >
                <motion.i
                  animate={{ rotate: [0, 90, 0] }}
                  transition={{ duration: 0.3 }}
                  className="fas fa-plus mr-2"
                ></motion.i>
                {t('add_payment')}
              </motion.button>
            </motion.div>
          </div>
        </animated.div>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <SearchFilter
              onSearch={handleSearch}
              onFilter={handleFilter}
              filters={statusFilters}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('view')}:</span>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'cards'
                ? (isDark ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
                : (isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                }`}
            >
              <i className="fas fa-th-large mr-1"></i> {t('cards')}
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'table'
                ? (isDark ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
                : (isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                }`}
            >
              <i className="fas fa-table mr-1"></i> {t('table')}
            </button>
          </div>
        </div>

        <BulkActions
          selectedItems={selectedPayments}
          onAction={handleBulkAction}
          actions={bulkActions}
        />

        {/* Content */}
        <AnimatePresence mode="wait">
          {viewMode === 'cards' ? (
            // Cards View
            <motion.div
              key="cards"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.9 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {trail.map((style, index) => {
                const payment = filteredPayments[index];
                if (!payment) return null;
                return (
                  <animated.div
                    key={payment.id}
                    style={style}
                  >
                    <motion.div
                      whileHover={{ scale: 1.02, y: -5 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 min-h-fit flex flex-col cursor-pointer will-change-transform animate-gpu"
                    >
                      {/* Header */}
                      <div className="flex justify-between items-start mb-4">
                        <motion.input
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          type="checkbox"
                          checked={selectedPayments.includes(payment.id)}
                          onChange={() => handlePaymentSelect(payment.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                        >
                          <StatusBadge
                            currentStatus={payment.status}
                            statusOptions={['pending', 'completed', 'failed', 'refunded']}
                            onStatusChange={(newStatus) => handleStatusUpdate(payment.id, newStatus)}
                          />
                        </motion.div>
                      </div>

                      {/* Payment Info */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="mb-4 flex-1"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <motion.h3
                            whileHover={{ scale: 1.05 }}
                            className="text-lg font-bold text-gray-900"
                          >
                            {t('payment')} #{payment.id}
                          </motion.h3>
                          <span className={`text-xs font-mono font-bold px-2 py-1 rounded-lg ${isDark ? 'bg-gray-700 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                            {getBookingCode(payment.booking_id)}
                          </span>
                        </div>

                        {/* Pemesan Info */}
                        {payment.booking?.user && (
                          <div className={`rounded-xl p-3 mb-3 ${isDark ? 'bg-gray-700/60' : 'bg-indigo-50'}`}>
                            <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDark ? 'text-gray-400' : 'text-indigo-500'}`}>
                              <i className="fas fa-user mr-1"></i> Pemesan
                            </div>
                            <div className={`font-semibold text-sm mb-1 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                              {payment.booking.user.name}
                            </div>
                            {payment.booking.user.email && (
                              <div className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <i className="fas fa-envelope mr-1"></i>{payment.booking.user.email}
                              </div>
                            )}
                            {payment.booking.user.phone && (
                              <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <i className="fas fa-phone mr-1"></i>{payment.booking.user.phone}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="space-y-2 text-sm">
                          <motion.p
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className="flex items-center text-gray-600"
                          >
                            <motion.i
                              animate={{ rotate: [0, 10, -10, 0] }}
                              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                              className={`fas fa-credit-card mr-2 ${isDark ? 'text-gray-400' : 'text-blue-500'}`}
                            ></motion.i>
                            <span className="truncate">{payment.payment_method}</span>
                          </motion.p>
                          <motion.p
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4, type: "spring" }}
                            className={`flex items-center font-semibold text-lg p-3 rounded-lg ${isDark
                              ? 'text-gray-300 bg-gray-700/50'
                              : 'text-green-600 bg-green-50'
                              }`}
                          >
                            <motion.i
                              animate={{ y: [0, -2, 0] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              className="fas fa-money-bill-wave mr-2"
                            ></motion.i>
                            Rp {Number(payment.amount).toLocaleString()}
                          </motion.p>
                          {payment.transaction_id && (
                            <motion.p
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.5 }}
                              className={`flex items-center text-xs p-2 rounded-lg ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'}`}
                            >
                              <i className="fas fa-hashtag mr-2"></i>
                              <span className="truncate">{payment.transaction_id}</span>
                            </motion.p>
                          )}
                          {payment.proof_image && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.6 }}
                            >
                              <a
                                href={getStorageUrl(payment.proof_image)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-blue-600 text-xs hover:text-blue-800 bg-blue-50 p-2 rounded-lg transition-colors"
                              >
                                <i className="fas fa-image mr-2"></i>
                                Lihat Bukti Transfer
                              </a>
                            </motion.div>
                          )}
                        </div>
                      </motion.div>

                      {/* Actions */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="flex gap-2"
                      >
                        <motion.button
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleEdit(payment)}
                          className={`flex-1 ${isDark ? 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700' : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'} text-white py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg`}
                        >
                          <motion.i
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 0.5 }}
                            className="fas fa-edit mr-1"
                          ></motion.i>
                          {t('edit')}
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => exportSingleRecord(payment, 'payment', 'payment', language)}
                          className={`${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'} text-white py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg`}
                          title={t('export_receipt')}
                        >
                          <motion.i
                            animate={{ y: [0, -2, 0] }}
                            transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                            className="fas fa-file-pdf"
                          ></motion.i>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: -5 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDelete(payment.id)}
                          className="bg-gradient-to-r from-gray-500 to-gray-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <motion.i
                            whileHover={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 0.3 }}
                            className="fas fa-trash"
                          ></motion.i>
                        </motion.button>
                      </motion.div>
                    </motion.div>
                  </animated.div>
                );
              })}
            </motion.div>
          ) : (
            // Table View
            <motion.div
              key="table"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.9 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                    <tr>
                      <th className="px-6 py-4 text-left">
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPayments(filteredPayments.map(p => p.id));
                            } else {
                              setSelectedPayments([]);
                            }
                          }}
                          checked={selectedPayments.length === filteredPayments.length && filteredPayments.length > 0}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('id')}</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Kode Pesanan</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Pemesan</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('amount')}</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('payment_method')}</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('status')}</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('transaction_id')}</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Bukti Bayar</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredPayments.map((payment, index) => (
                      <tr key={payment.id} className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 ${selectedPayments.includes(payment.id) ? 'bg-gradient-to-r from-blue-50 to-purple-50' : index % 2 === 0 ? 'bg-white/50' : 'bg-slate-50/50'}`}>
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedPayments.includes(payment.id)}
                            onChange={() => handlePaymentSelect(payment.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">#{payment.id}</td>
                        <td className="px-6 py-4">
                          <span className="text-xs font-mono font-bold bg-blue-50 text-blue-700 px-2 py-1 rounded-lg">{getBookingCode(payment.booking_id)}</span>
                        </td>
                        <td className="px-6 py-4">
                          {payment.booking?.user ? (
                            <div>
                              <div className="text-sm font-semibold text-gray-900">{payment.booking.user.name}</div>
                              <div className="text-xs text-gray-500 truncate max-w-xs">{payment.booking.user.email}</div>
                              {payment.booking.user.phone && <div className="text-xs text-gray-400">{payment.booking.user.phone}</div>}
                            </div>
                          ) : <span className="text-xs text-gray-400">-</span>}
                        </td>
                        <td className={`px-6 py-4 text-sm font-bold ${isDark ? 'text-gray-300' : 'text-green-600'}`}>Rp {Number(payment.amount).toLocaleString()}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{payment.payment_method}</td>
                        <td className="px-6 py-4">
                          <StatusBadge
                            currentStatus={payment.status}
                            statusOptions={['pending', 'completed', 'failed', 'refunded']}
                            onStatusChange={(newStatus) => handleStatusUpdate(payment.id, newStatus)}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{payment.transaction_id || '-'}</td>
                        <td className="px-6 py-4">
                          {payment.proof_image ? (
                            <a href={getStorageUrl(payment.proof_image)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 transition-colors">
                              <i className="fas fa-image mr-1"></i> Lihat Bukti
                            </a>
                          ) : <span className="text-xs text-gray-400">Belum ada</span>}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEdit(payment)}
                              className={`${isDark ? 'bg-gray-500 hover:bg-gray-600' : 'bg-blue-500 hover:bg-blue-600'} text-white px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200`}
                            >
                              {t('edit')}
                            </button>
                            <button
                              onClick={() => exportSingleRecord(payment, 'payment', 'payment', language)}
                              className={`${isDark ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'} text-white px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200`}
                              title={t('export_receipt')}
                            >
                              <i className="fas fa-file-pdf"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(payment.id)}
                              className="bg-gray-500 text-white px-2 py-1 rounded-lg text-xs font-medium hover:bg-red-600 transition-all duration-200"
                            >
                              {t('delete')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {filteredPayments.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-12 text-center"
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 15, delay: 0.2 }}
                className="text-6xl mb-4 text-gray-400"
              >
                <i className="fas fa-credit-card"></i>
              </motion.div>
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-xl font-semibold text-gray-900 mb-2"
              >
                {t('no_payments_found')}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-gray-600"
              >
                {t('try_adjusting_search')}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showForm && (
            <PaymentForm
              payment={editPayment}
              onSave={handleFormSave}
              onCancel={() => { setShowForm(false); setEditPayment(null); }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </PageTransition>
  );
}